import Image from 'next/image'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import type {Block} from '@/services/kontent-ai/types'
import {Button} from '@/products/flexible-web-toolkit/components/button/button'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {loadImageFromKontentAI} from '@/image-loader'

export type CouponContentItem = IContentItem<{
	title: Elements.TextElement
	description: Elements.TextElement
	buttonText: Elements.TextElement
	footerText: Elements.TextElement
	logo: Elements.AssetsElement
	image: Elements.AssetsElement
	url: Elements.TextElement
	brandColor: Elements.MultipleChoiceElement
	brandColorCustom: Elements.CustomElement
}>

export const CouponBlock: Block<CouponContentItem> = ({block, page}) => {
	// Color priority system: block level > page level > fallback (using shared hook)
	const {bgClass: finalBgClass} = colorWithFallback({
		blockColor: block.elements.brandColorCustom,
		blockColorLegacy: block.elements.brandColor,
		pageConfig: page.elements.config[0],
		fallbackColorId: 'primary',
	})

	const buttonCTAcolor = finalBgClass

	return (
		<div className="mx-auto mt-20 max-w-3xl rounded-lg border-2">
			<div className="flex flex-col px-4 py-6 md:flex-row md:px-12 md:py-10">
				<div className="w-full flex-col items-center pb-5 md:mr-6 md:w-1/3 md:p-0">
					{block.elements.logo[0]?.url ? (
						<Image
							alt={
								block.elements.logo[0].description ||
								'coupon-logo'
							}
							className="mx-auto h-1/4 w-1/2 object-contain pb-2.5 md:mx-0 md:w-full"
							height={block.elements.logo[0].height || 100}
							loader={loadImageFromKontentAI}
							src={block.elements.logo[0].url}
							width={block.elements.logo[0].width || 200}
						/>
					) : null}
					{block.elements.image[0]?.url ? (
						<Image
							alt={
								block.elements.image[0].description || 'coupon'
							}
							className="h-full w-full object-cover md:h-3/4"
							height={block.elements.image[0].height || 300}
							loader={loadImageFromKontentAI}
							src={block.elements.image[0].url}
							width={block.elements.image[0].width || 400}
						/>
					) : null}
				</div>
				<div className="w-full md:w-2/3">
					<h2 className="mb-5 text-2xl md:text-4xl">
						{block.elements.title}
					</h2>
					<p>{block.elements.description}</p>
					<Button
						className={`${buttonCTAcolor} mr-6 mt-6 w-full md:w-1/2`}
						href={block.elements.url}
						variant={buttonCTAcolor}
					>
						{block.elements.buttonText}
					</Button>
				</div>
			</div>
			<div className="flex bg-gray-300 px-12 py-5">
				<p className="text-xs text-gray-600">
					{block.elements.footerText}
				</p>
			</div>
		</div>
	)
}
